import { apiSlice } from "../../../features/api/apiSlice";

export type UserCategory = {
  id: number;
  name: string;
  is_active: boolean;
};

export type MembershipBand = {
  id: number;
  user_category_id: number;
  label: string;
  min_years: number;
  max_years?: number | null;
  discount_percentage: number;
  is_active: boolean;

  category?: UserCategory;

  created_at?: string;
  updated_at?: string;
};

export type MembershipBandPayload = {
  user_category_id: number;
  label: string;
  min_years: number;
  max_years?: number | null;
  discount_percentage?: number;
  is_active?: boolean;
};

export type Paged<T> = {
  data: T[];
  current_page?: number;
  last_page?: number;
  per_page?: number;
  total?: number;
};

function pick(res: any) {
  return res?.data?.data ?? res?.data ?? res;
}

function pickArray(res: any) {
  const d = pick(res);
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.data)) return d.data;
  if (Array.isArray(res?.data)) return res.data;
  return [];
}

function pickPage<T>(res: any): Paged<T> {
  const d = res?.data?.data ?? res?.data ?? res;
  if (Array.isArray(d)) return { data: d };
  if (Array.isArray(d?.data)) return d;
  return { data: [] };
}

export const membershipBandsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // =========================
    // Public list for dropdowns
    // GET /membership-bands?user_category_id=&q=&include=category
    // =========================
    getMembershipBands: builder.query<
      MembershipBand[],
      { user_category_id?: number; q?: string; include?: "category" } | void
    >({
      query: (arg) => {
        const a = (arg ?? {}) as any;
        const qs = new URLSearchParams();
        if (a.user_category_id)
          qs.set("user_category_id", String(a.user_category_id));
        if (a.q) qs.set("q", a.q);
        if (a.include) qs.set("include", a.include);
        const q = qs.toString();
        return q
          ? `/members/membership-bands?${q}`
          : `/members/membership-bands`;
      },
      transformResponse: (res: any) => {
        return pickArray(res);
      },
      providesTags: (result) => {
        const list = Array.isArray(result) ? result : [];
        return list.length
          ? [
              ...list.map((x) => ({
                type: "MembershipBands" as const,
                id: x.id,
              })),
              { type: "MembershipBands" as const, id: "LIST" },
            ]
          : [{ type: "MembershipBands" as const, id: "LIST" }];
      },
    }),

    // =========================
    // Admin CRUD
    // /admin/billing/membership-bands
    // =========================
    getAdminMembershipBands: builder.query<
      Paged<MembershipBand>,
      { q?: string; user_category_id?: number } | void
    >({
      query: (arg) => {
        const a = (arg ?? {}) as any;
        const qs = new URLSearchParams();
        if (a.q) qs.set("q", a.q);
        if (a.user_category_id)
          qs.set("user_category_id", String(a.user_category_id));
        const q = qs.toString();
        return q
          ? `/members/membership-bands?${q}`
          : `/members/membership-bands`;
      },
      transformResponse: (res: any) => {
        console.log(res, "tjhios os tje forst resons");
        return pickPage<MembershipBand>(res);
      },
      providesTags: (result) => {
        const list = result?.data ?? [];
        return list.length
          ? [
              ...list.map((x) => ({
                type: "MembershipBands" as const,
                id: x.id,
              })),
              { type: "MembershipBands" as const, id: "LIST" },
            ]
          : [{ type: "MembershipBands" as const, id: "LIST" }];
      },
    }),

    getAdminMembershipBandDetails: builder.query<MembershipBand, number>({
      query: (id) => `/members/membership-bands/${id}`,
      transformResponse: (res: any) => pick(res),
      providesTags: (_r, _e, id) => [{ type: "MembershipBands" as const, id }],
    }),

    createMembershipBand: builder.mutation<any, MembershipBandPayload>({
      query: (body) => ({
        url: `/members/membership-bands`,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "MembershipBands" as const, id: "LIST" }],
    }),

    updateMembershipBand: builder.mutation<
      any,
      { id: number; body: Partial<MembershipBandPayload> }
    >({
      query: ({ id, body }) => ({
        url: `/members/membership-bands/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: "MembershipBands" as const, id: arg.id },
        { type: "MembershipBands" as const, id: "LIST" },
      ],
    }),

    deleteMembershipBand: builder.mutation<any, number>({
      query: (id) => ({
        url: `/members/billing/membership-bands/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "MembershipBands" as const, id: "LIST" }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetMembershipBandsQuery,

  useGetAdminMembershipBandsQuery,
  useGetAdminMembershipBandDetailsQuery,
  useCreateMembershipBandMutation,
  useUpdateMembershipBandMutation,
  useDeleteMembershipBandMutation,
} = membershipBandsApi;
